import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MdUploadFile, MdCloudUpload, MdPreview, MdCheckCircle, MdError, MdWarning, MdClose } from 'react-icons/md';
import { saudiService } from '../../services/saudiService';
import LoadingSpinner from '../../components/shared/LoadingSpinner';

const STATUS_BADGE = {
  completed: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  success: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  warning: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
  error: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
  failed: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
  partial: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
  processing: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  pending: 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-400',
};

const IngestionPage = () => {
  // Single upload state
  const [singleFile, setSingleFile] = useState(null);
  const [reportDate, setReportDate] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState(null);
  const singleRef = useRef(null);

  // Bulk upload state
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkMsg, setBulkMsg] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const bulkRef = useRef(null);

  // Duplicate handling
  const [duplicatePrompt, setDuplicatePrompt] = useState(null);

  // Upload history
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const fetchHistory = useCallback(async () => {
    try {
      setHistoryLoading(true);
      const res = await saudiService.getUploadHistory();
      setHistory(res.data?.uploads || res.data || []);
    } catch {
      // silently fail
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  // Preview
  const handlePreview = async () => {
    if (!singleFile) return;
    setPreviewing(true);
    setPreviewData(null);
    try {
      const res = await saudiService.previewExtract(singleFile);
      setPreviewData(res.data);
    } catch (err) {
      setUploadMsg({ type: 'error', text: err.response?.data?.error || 'Preview failed' });
    } finally {
      setPreviewing(false);
    }
  };

  // Single upload
  const handleUpload = async (duplicateAction) => {
    if (!singleFile) return;
    setUploading(true);
    setUploadMsg(null);
    setDuplicatePrompt(null);
    try {
      const res = await saudiService.uploadReport(singleFile, reportDate || undefined, duplicateAction);
      const d = res.data;
      const warnings = d.validation?.warnings || [];
      const msgType = warnings.length > 0 ? 'warning' : 'success';
      const msgText = warnings.length > 0
        ? `Uploaded ${d.records_created || 0} records with ${warnings.length} warning(s): ${warnings.join('; ')}`
        : `Uploaded ${d.records_created || 0} records for ${d.report_date}`;
      setUploadMsg({ type: msgType, text: msgText });
      setSingleFile(null);
      setPreviewData(null);
      setReportDate('');
      if (singleRef.current) singleRef.current.value = '';
      fetchHistory();
    } catch (err) {
      const data = err.response?.data;
      if (data?.duplicate) {
        setDuplicatePrompt(data);
      } else {
        setUploadMsg({ type: 'error', text: data?.error || 'Upload failed' });
      }
    } finally {
      setUploading(false);
    }
  };

  // Bulk upload
  const [bulkResults, setBulkResults] = useState(null);
  const handleBulkUpload = async () => {
    if (bulkFiles.length === 0) return;
    setBulkUploading(true);
    setBulkMsg(null);
    setBulkResults(null);
    try {
      const res = await saudiService.bulkUpload(bulkFiles);
      const results = res.data?.results || [];
      setBulkResults(results);
      const completed = results.filter(r => r.status === 'completed' || r.status === 'warning').length;
      const skipped = results.filter(r => r.status === 'skipped').length;
      const failed = results.filter(r => r.status === 'failed').length;
      const parts = [];
      if (completed) parts.push(`${completed} uploaded`);
      if (skipped) parts.push(`${skipped} skipped (duplicate)`);
      if (failed) parts.push(`${failed} failed`);
      setBulkMsg({ type: failed > 0 ? 'warning' : 'success', text: parts.join(', ') || 'Done' });
      setBulkFiles([]);
      fetchHistory();
    } catch (err) {
      setBulkMsg({ type: 'error', text: err.response?.data?.error || `Bulk upload failed — ${err.message || 'timeout?'}` });
    } finally {
      setBulkUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files).filter(f => f.name.endsWith('.pdf'));
    setBulkFiles(prev => [...prev, ...files]);
  };

  const removeBulkFile = (idx) => {
    setBulkFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const inputCls = 'bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 text-sm text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-[#c9a96e]/30 focus:border-[#c9a96e] outline-none';
  const btnPrimary = 'px-4 py-2 rounded-lg text-sm font-medium bg-[#c9a96e] text-white hover:bg-[#b8954f] disabled:opacity-50 disabled:cursor-not-allowed transition-colors';
  const btnSecondary = 'px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 transition-colors';

  return (
    <div className="space-y-6">
      {/* Single Upload */}
      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg p-4">
        <h3 className="font-serif text-base text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
          <MdUploadFile className="text-[#c9a96e]" />
          Single Upload
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1 block">Weekly PDF Report</label>
            <input
              ref={singleRef}
              type="file"
              accept=".pdf"
              onChange={(e) => {
                setSingleFile(e.target.files[0] || null);
                setPreviewData(null);
                setUploadMsg(null);
              }}
              className="block w-full text-sm text-gray-800 dark:text-gray-200 file:mr-2 file:py-1.5 file:px-3 file:rounded-lg file:border file:border-gray-200 dark:file:border-gray-700 file:text-sm file:bg-white dark:file:bg-[#0d1117] file:text-gray-700 dark:file:text-gray-300 hover:file:bg-gray-50 dark:hover:file:bg-gray-800 cursor-pointer"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1 block">Report Date (optional)</label>
            <input
              type="date"
              value={reportDate}
              onChange={(e) => setReportDate(e.target.value)}
              className={`${inputCls} w-full`}
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={handlePreview}
              disabled={!singleFile || previewing}
              className={btnSecondary}
            >
              {previewing ? 'Extracting...' : 'Preview'}
            </button>
            <button
              onClick={() => handleUpload(null)}
              disabled={!singleFile || uploading}
              className={btnPrimary}
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </div>

        {/* Upload messages */}
        {uploadMsg && (
          <div className={`mt-3 flex items-start gap-2 rounded-lg px-4 py-2.5 text-sm ${
            uploadMsg.type === 'success'
              ? 'border border-green-300 dark:border-green-800 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
              : uploadMsg.type === 'warning'
              ? 'border border-yellow-300 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400'
              : 'border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
          }`}>
            {uploadMsg.type === 'success' ? <MdCheckCircle className="mt-0.5 shrink-0" /> : uploadMsg.type === 'warning' ? <MdWarning className="mt-0.5 shrink-0" /> : <MdError className="mt-0.5 shrink-0" />}
            <span className="flex-1">{uploadMsg.text}</span>
            <button onClick={() => setUploadMsg(null)} className="ml-auto shrink-0"><MdClose /></button>
          </div>
        )}

        {/* Duplicate prompt */}
        {duplicatePrompt && (
          <div className="mt-3 border border-yellow-400 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2 text-yellow-700 dark:text-yellow-400">
              <MdWarning className="text-lg" />
              <span className="text-sm font-medium">Duplicate Data Detected</span>
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
              {duplicatePrompt.message || 'Data for this report date already exists.'}
            </p>
            <div className="flex gap-2">
              <button onClick={() => handleUpload('replace')} className={btnPrimary}>
                Replace Existing
              </button>
              <button onClick={() => handleUpload('skip')} className={btnSecondary}>
                Skip Duplicates
              </button>
              <button onClick={() => setDuplicatePrompt(null)} className={btnSecondary}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Preview panel */}
        {previewing && <LoadingSpinner message="Extracting via OCR — this takes 15-30 seconds..." />}
        {previewData && !previewing && (
          <div className="mt-4 space-y-3">
            {/* Header */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                <MdPreview className="text-[#c9a96e]" />
                Preview: {previewData.report_date || 'Date not detected'}
              </span>
              <button onClick={() => setPreviewData(null)} className="text-gray-400 hover:text-gray-600"><MdClose /></button>
            </div>

            {/* Validation results */}
            {previewData.validation && (
              <div className="space-y-1.5">
                {previewData.validation.passed?.map((msg, i) => (
                  <div key={`p${i}`} className="flex items-center gap-2 text-xs text-green-600 dark:text-green-400">
                    <MdCheckCircle className="shrink-0" /> {msg}
                  </div>
                ))}
                {previewData.validation.warnings?.map((msg, i) => (
                  <div key={`w${i}`} className="flex items-center gap-2 text-xs text-yellow-600 dark:text-yellow-400">
                    <MdWarning className="shrink-0" /> {msg}
                  </div>
                ))}
              </div>
            )}

            {/* Extraction errors */}
            {previewData.errors?.length > 0 && (
              <div className="space-y-1">
                {previewData.errors.map((err, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-red-600 dark:text-red-400">
                    <MdError className="shrink-0" /> {err}
                  </div>
                ))}
              </div>
            )}

            {/* Value Traded table */}
            {previewData.value_traded?.length > 0 && (
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <div className="bg-gray-50 dark:bg-[#0d1117] px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    Value Traded — {previewData.value_traded.length} rows
                  </span>
                </div>
                <div className="overflow-x-auto max-h-80">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 dark:bg-[#0d1117] sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">Nationality</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">Investor Type</th>
                        <th className="px-3 py-2 text-right font-medium text-gray-500 dark:text-gray-400">Buy (SAR)</th>
                        <th className="px-3 py-2 text-right font-medium text-gray-500 dark:text-gray-400">Sell (SAR)</th>
                        <th className="px-3 py-2 text-right font-medium text-gray-500 dark:text-gray-400">Net (SAR)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {previewData.value_traded.map((row, i) => (
                        <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          <td className="px-3 py-1.5 text-gray-800 dark:text-gray-200">{row.nationality}</td>
                          <td className="px-3 py-1.5 text-gray-800 dark:text-gray-200">{row.investor_type}</td>
                          <td className="px-3 py-1.5 text-right text-gray-800 dark:text-gray-200 tabular-nums">{Number(row.buy_value).toLocaleString()}</td>
                          <td className="px-3 py-1.5 text-right text-gray-800 dark:text-gray-200 tabular-nums">{Number(row.sell_value).toLocaleString()}</td>
                          <td className={`px-3 py-1.5 text-right tabular-nums font-medium ${row.net_value >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {row.net_value >= 0 ? '+' : ''}{Number(row.net_value).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                      {/* Totals row */}
                      <tr className="bg-gray-50 dark:bg-[#0d1117] font-medium border-t-2 border-gray-300 dark:border-gray-600">
                        <td className="px-3 py-2 text-gray-900 dark:text-gray-100" colSpan={2}>TOTAL</td>
                        <td className="px-3 py-2 text-right text-gray-900 dark:text-gray-100 tabular-nums">
                          {previewData.value_traded.reduce((s, r) => s + Number(r.buy_value), 0).toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-900 dark:text-gray-100 tabular-nums">
                          {previewData.value_traded.reduce((s, r) => s + Number(r.sell_value), 0).toLocaleString()}
                        </td>
                        <td className={`px-3 py-2 text-right tabular-nums ${
                          (() => { const n = previewData.value_traded.reduce((s, r) => s + Number(r.net_value), 0); return n >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'; })()
                        }`}>
                          {(() => { const n = previewData.value_traded.reduce((s, r) => s + Number(r.net_value), 0); return (n >= 0 ? '+' : '') + n.toLocaleString(); })()}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Ownership table */}
            {previewData.ownership?.length > 0 && (
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <div className="bg-gray-50 dark:bg-[#0d1117] px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    Ownership — {previewData.ownership.length} rows
                  </span>
                </div>
                <div className="overflow-x-auto max-h-80">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 dark:bg-[#0d1117] sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">Nationality</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">Investor Type</th>
                        <th className="px-3 py-2 text-right font-medium text-gray-500 dark:text-gray-400">Holding (SAR)</th>
                        <th className="px-3 py-2 text-right font-medium text-gray-500 dark:text-gray-400">Issued %</th>
                        <th className="px-3 py-2 text-right font-medium text-gray-500 dark:text-gray-400">Free Float %</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {previewData.ownership.map((row, i) => (
                        <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          <td className="px-3 py-1.5 text-gray-800 dark:text-gray-200">{row.nationality}</td>
                          <td className="px-3 py-1.5 text-gray-800 dark:text-gray-200">{row.investor_type}</td>
                          <td className="px-3 py-1.5 text-right text-gray-800 dark:text-gray-200 tabular-nums">{Number(row.holding_value).toLocaleString()}</td>
                          <td className="px-3 py-1.5 text-right text-gray-800 dark:text-gray-200 tabular-nums">{Number(row.issued_pct).toFixed(2)}%</td>
                          <td className="px-3 py-1.5 text-right text-gray-800 dark:text-gray-200 tabular-nums">{Number(row.free_float_pct).toFixed(2)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bulk Upload */}
      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg p-4">
        <h3 className="font-serif text-base text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
          <MdCloudUpload className="text-[#c9a96e]" />
          Bulk Upload
        </h3>

        {/* Drop zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => bulkRef.current?.click()}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
            dragOver
              ? 'border-[#c9a96e] bg-[#c9a96e]/5'
              : 'border-gray-300 dark:border-gray-700 hover:border-[#c9a96e]/50'
          }`}
        >
          <MdCloudUpload className="text-3xl text-gray-400 dark:text-gray-600 mx-auto mb-2" />
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Drop PDF files here or <span className="text-[#c9a96e] font-medium">browse</span>
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-600 mt-1">.pdf</p>
          <input
            ref={bulkRef}
            type="file"
            multiple
            accept=".pdf"
            className="hidden"
            onChange={(e) => setBulkFiles(prev => [...prev, ...Array.from(e.target.files)])}
          />
        </div>

        {/* Queued files */}
        {bulkFiles.length > 0 && (
          <div className="mt-3 space-y-1">
            {bulkFiles.map((f, i) => (
              <div key={i} className="flex items-center justify-between bg-gray-50 dark:bg-[#0d1117] rounded-lg px-3 py-2 text-sm">
                <span className="text-gray-800 dark:text-gray-200 truncate">{f.name}</span>
                <button onClick={() => removeBulkFile(i)} className="text-gray-400 hover:text-red-500 ml-2"><MdClose /></button>
              </div>
            ))}
            <div className="flex gap-2 mt-2">
              <button onClick={handleBulkUpload} disabled={bulkUploading} className={btnPrimary}>
                {bulkUploading ? 'Uploading...' : `Upload ${bulkFiles.length} files`}
              </button>
              <button onClick={() => setBulkFiles([])} className={btnSecondary}>Clear</button>
            </div>
          </div>
        )}

        {bulkMsg && (
          <div className={`mt-3 flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm ${
            bulkMsg.type === 'success'
              ? 'border border-green-300 dark:border-green-800 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
              : bulkMsg.type === 'warning'
              ? 'border border-yellow-300 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400'
              : 'border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
          }`}>
            {bulkMsg.type === 'success' ? <MdCheckCircle /> : bulkMsg.type === 'warning' ? <MdWarning /> : <MdError />}
            {bulkMsg.text}
          </div>
        )}

        {/* Per-file results */}
        {bulkResults && bulkResults.length > 0 && (
          <div className="mt-3 space-y-1">
            {bulkResults.map((r, i) => (
              <div key={i} className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded ${
                r.status === 'completed' ? 'text-green-600 dark:text-green-400' :
                r.status === 'warning' ? 'text-yellow-600 dark:text-yellow-400' :
                r.status === 'skipped' ? 'text-gray-500 dark:text-gray-400' :
                'text-red-600 dark:text-red-400'
              }`}>
                {r.status === 'completed' ? <MdCheckCircle className="shrink-0" /> :
                 r.status === 'warning' ? <MdWarning className="shrink-0" /> :
                 r.status === 'skipped' ? <MdClose className="shrink-0" /> :
                 <MdError className="shrink-0" />}
                <span className="truncate">{r.file}</span>
                <span className="ml-auto shrink-0">
                  {r.date && `${r.date} · `}{r.records ? `${r.records} records` : r.error || r.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upload History */}
      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800">
          <h3 className="font-serif text-base text-gray-900 dark:text-gray-100">Upload History</h3>
        </div>
        {historyLoading ? (
          <LoadingSpinner message="Loading history..." />
        ) : history.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <MdUploadFile className="text-3xl mx-auto mb-2 opacity-40" />
            <p className="text-sm">No uploads yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-[#0d1117]">
                <tr>
                  <th className="px-4 py-2.5 text-left text-[10px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Report Date</th>
                  <th className="px-4 py-2.5 text-left text-[10px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Filename</th>
                  <th className="px-4 py-2.5 text-center text-[10px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Records</th>
                  <th className="px-4 py-2.5 text-center text-[10px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Status</th>
                  <th className="px-4 py-2.5 text-left text-[10px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Uploaded</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {history.map((h, i) => (
                  <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-4 py-2.5 text-gray-900 dark:text-gray-100 font-medium whitespace-nowrap">
                      {h.report_date}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600 dark:text-gray-400 truncate max-w-[200px]">
                      {h.filename || h.file_name || '-'}
                    </td>
                    <td className="px-4 py-2.5 text-center text-gray-700 dark:text-gray-300">
                      {h.records_created || '-'}
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium uppercase ${STATUS_BADGE[h.status] || STATUS_BADGE.success}`}>
                        {h.status || 'success'}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-gray-500 dark:text-gray-400 whitespace-nowrap text-xs">
                      {h.uploaded_at ? new Date(h.uploaded_at).toLocaleString() : h.created_at ? new Date(h.created_at).toLocaleString() : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default IngestionPage;
